import { useCallback, useMemo, useRef, useState } from 'react';
import { useNow, useRigg } from '@/data/RiggProvider.tsx';
import { FollowUpDetail, SuggestedRow, suggestedFollowUps } from '@/components/FollowUps.tsx';
import { BoardLayout, LIST_CARD, ListSearch, useListKeys } from '@/components/Board.tsx';
import { Empty, Shimmer } from '@/components/ui.tsx';
import { plural } from '@/lib/format.ts';
import { navigate } from '@/lib/router.ts';

/**
 * Suggested — follow-ups routines offered, waiting on a yes or a no.
 *
 * Its own page rather than a block at the top of Tasks: a long run of
 * suggestions pushed the work actually in flight below the fold, and a
 * suggestion is not work anybody has asked for yet. Laid out like Tasks all
 * the same — a list that scrolls on its own, a detail pane beside it — so
 * going through twenty of them works the way going through twenty tasks does.
 */
export function SuggestedScreen({ selectedId }: { selectedId: string | null }) {
  const { data } = useRigg();
  const now = useNow();
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const all = useMemo(() => suggestedFollowUps(data.followUps), [data.followUps]);
  const rows = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return all;
    return all.filter((followUp) => {
      const origin = data.tasks[followUp.task_id];
      const text = `${followUp.title}\n${followUp.prompt}\n${origin ? `#${origin.ref} ${origin.title}` : ''}`.toLowerCase();
      return words.every((word) => text.includes(word));
    });
  }, [all, query, data.tasks]);

  // A suggestion decided elsewhere drops out of the list; its pane goes with it.
  const selected = selectedId ? (all.find((f) => f.id === selectedId) ?? null) : null;

  const select = useCallback(
    (id: string | null) => navigate(id ? `/suggested/${id}` : '/suggested', true),
    [],
  );

  const ids = useMemo(() => rows.map((f) => f.id), [rows]);
  useListKeys(ids, selectedId, select, searchRef);

  // Rejecting from the pane moves on to the next one rather than leaving an
  // empty pane — the point of the page is getting through the pile.
  const afterRejected = () => {
    const at = ids.indexOf(selectedId ?? '');
    select(ids[at + 1] ?? ids[at - 1] ?? null);
  };

  return (
    <BoardLayout
      title={query.trim() ? 'Search' : 'Suggested'}
      blurb="Follow-ups a routine proposed while it worked, offered once the task they came from has merged. Open one to edit and accept it as a task, or reject it to take it off the list."
      toolbar={
        <>
          <ListSearch
            inputRef={searchRef}
            value={query}
            onChange={setQuery}
            placeholder="Search, #58"
            label="Search suggestions by text or the task they came from"
            title="Searches each suggestion's title and prompt, and the task it came from. Press / to jump here."
          />
          {data.loaded ? (
            <span className="ml-auto pr-1 text-[12.5px] text-mute">{plural(all.length, 'suggestion')}</span>
          ) : null}
        </>
      }
      detail={
        selected ? (
          <FollowUpDetail
            key={selected.id}
            followUp={selected}
            onClose={() => select(null)}
            onRejected={afterRejected}
          />
        ) : null
      }
      nothingSelected="Choose a suggestion on the left — arrow keys work too."
    >
      {!data.loaded ? (
        <div className="flex flex-col gap-2 px-3 sm:px-0">
          {[0, 1, 2, 3].map((n) => (
            <Shimmer key={n} className="h-[76px] rounded-2xl" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="px-3 sm:px-0">
          {all.length > 0 ? (
            <Empty title="No matching suggestions" hint="Try fewer words." />
          ) : (
            <Empty
              title="Nothing suggested"
              hint="When a routine spots work beyond its task, it shows up here once that task lands."
            />
          )}
        </div>
      ) : (
        <div className={LIST_CARD}>
          {rows.map((followUp) => (
            <SuggestedRow
              key={followUp.id}
              followUp={followUp}
              origin={data.tasks[followUp.task_id] ?? null}
              agentName={followUp.agent_id ? (data.agents[followUp.agent_id]?.name ?? null) : null}
              now={now}
              selected={followUp.id === selectedId}
              onSelect={select}
            />
          ))}
        </div>
      )}
    </BoardLayout>
  );
}
